import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { 
  Calendar, 
  Clock, 
  Phone, 
  Mail, 
  User, 
  Building, 
  MapPin, 
  Search,
  Filter,
  CheckCircle,
  XCircle,
  AlertCircle,
  TrendingUp,
  Users,
  CalendarDays,
  Trash2,
  LogOut,
  Loader2
} from "lucide-react";
import { format } from "date-fns";
import { supabase } from "@/integrations/supabase/client";

// Mock data - En producción vendrá de Supabase
const mockLeads = [
  {
    id: 1,
    name: "Juan Pérez",
    email: "juan@solartechsl.com",
    phone: "+34 600 123 456",
    company: "Solar Tech S.L.",
    service_interest: "Instaladores de placas solares",
    location: "Madrid",
    status: "nuevo",
    created_at: "2024-01-15T10:30:00Z",
    message: "Tipo de empresa: Instaladores de placas solares. Plan de interés: Plan Profesional - Generación de Leads. Ubicación: Madrid. Llevamos 3 años en el sector, necesitamos más visibilidad online y generar más leads cualificados."
  },
  {
    id: 2,
    name: "María García",
    email: "maria@reformasgarcia.com",
    phone: "+34 699 987 654",
    company: "Reformas García",
    service_interest: "Reformas del hogar",
    location: "Barcelona",
    status: "contactado",
    created_at: "2024-01-14T15:45:00Z",
    message: "Tipo de empresa: Reformas del hogar. Plan de interés: Plan Premium - Marketing Completo. Ubicación: Barcelona. Empresa familiar con 15 años de experiencia, queremos expandir nuestro negocio digitalmente."
  },
  {
    id: 3,
    name: "Carlos López",
    email: "carlos@ecoenergia.com",
    phone: "+34 611 555 777",
    company: "EcoEnergía Valencia",
    service_interest: "Instaladores de placas solares",
    location: "Valencia",
    status: "calificado",
    created_at: "2024-01-13T09:15:00Z",
    message: "Tipo de empresa: Instaladores de placas solares. Plan de interés: Plan Básico - Presencia Online. Ubicación: Valencia. Empresa nueva que necesita presencia online básica para empezar."
  }
];

const mockAppointments = [
  {
    id: 1,
    lead_id: 1,
    date: "2024-01-20T10:00:00Z",
    service_type: "Instaladores de placas solares",
    status: "programada",
    notes: "Primera consulta - Plan Profesional",
    lead: mockLeads[0]
  },
  {
    id: 2,
    lead_id: 2,
    date: "2024-01-21T15:30:00Z",
    service_type: "Reformas del hogar",
    status: "confirmada",
    notes: "Consulta - Plan Premium",
    lead: mockLeads[1]
  }
];

export function AdminDashboard() {
  const [leads, setLeads] = useState<any[]>([]);
  const [appointments, setAppointments] = useState<any[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("todos");
  const [isLoading, setIsLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  const { toast } = useToast();
  const { signOut, user } = useAuth();

  // Check if user has admin role
  useEffect(() => {
    const checkAdminRole = async () => {
      if (!user) {
        setIsAdmin(false);
        return;
      }
      
      try {
        const { data, error } = await supabase
          .from('user_roles')
          .select('role')
          .eq('user_id', user.id)
          .eq('role', 'admin')
          .single();
        
        setIsAdmin(!!data && !error);
      } catch (error) {
        console.error('Error checking admin role:', error);
        setIsAdmin(false);
      }
    };

    checkAdminRole();
  }, [user]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        
        // Fetch leads from Supabase
        const { data: leadsData, error: leadsError } = await supabase
          .from('leads')
          .select('*')
          .order('created_at', { ascending: false });

        if (leadsError) throw leadsError;

        // Fetch appointments with lead info
        const { data: appointmentsData, error: appointmentsError } = await supabase
          .from('appointments')
          .select(`
            *,
            leads (
              id, name, email, phone, company
            )
          `)
          .order('created_at', { ascending: false });

        if (appointmentsError) throw appointmentsError;

        setLeads(leadsData || []);
        setAppointments(appointmentsData || []);
      } catch (error) {
        console.error('Error fetching data:', error);
        // Fallback to mock data
        setLeads(mockLeads);
        setAppointments(mockAppointments);
        toast({
          title: "Error de conexión",
          description: "No se pudo conectar con la base de datos. Mostrando datos de ejemplo.",
          variant: "destructive"
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  const leadStatuses = {
    nuevo: { label: "Nuevo", color: "bg-blue-500", icon: AlertCircle },
    contactado: { label: "Contactado", color: "bg-yellow-500", icon: Phone },
    calificado: { label: "Calificado", color: "bg-green-500", icon: CheckCircle },
    perdido: { label: "Perdido", color: "bg-red-500", icon: XCircle }
  };

  const appointmentStatuses = {
    programada: { label: "Programada", color: "bg-blue-500" },
    confirmada: { label: "Confirmada", color: "bg-green-500" },
    completada: { label: "Completada", color: "bg-gray-500" },
    cancelada: { label: "Cancelada", color: "bg-red-500" }
  };

  const updateLeadStatus = async (leadId: string, newStatus: string) => {
    try {
      const { error } = await supabase
        .from('leads')
        .update({ status: newStatus })
        .eq('id', leadId);

      if (error) throw error;

      setLeads(leads.map(lead => 
        lead.id === leadId ? { ...lead, status: newStatus } : lead
      ));
      
      toast({
        title: "Estado actualizado",
        description: "El estado del lead ha sido actualizado correctamente"
      });
    } catch (error) {
      console.error('Error updating lead status:', error);
      toast({
        title: "Error",
        description: "No se pudo actualizar el estado del lead",
        variant: "destructive"
      });
    }
  };

  const generateSampleLeads = async () => {
    const sampleLeads = [
      {
        name: "Ana Martínez",
        email: "ana.martinez@solarmadrid.com",
        phone: "+34 654 321 987",
        company: "Solar Madrid",
        source: "website",
        status: "new",
        notes: "Tipo de empresa: Instaladores de placas solares. Plan de interés: Plan Profesional - Generación de Leads. Ubicación: Madrid. Empresa con 5 años en el sector, buscan aumentar su cartera de clientes residenciales."
      },
      {
        name: "Roberto Sánchez",
        email: "roberto@reformasevilla.com",
        phone: "+34 677 888 999",
        company: "Reformas Sevilla Plus",
        source: "facebook_ads",
        status: "new",
        notes: "Tipo de empresa: Reformas del hogar. Plan de interés: Plan Premium - Marketing Completo. Ubicación: Sevilla. Empresa familiar que quiere digitalizarse y llegar a más clientes."
      },
      {
        name: "Carmen Jiménez",
        email: "carmen@energiasolar.com",
        phone: "+34 621 456 789",
        company: "Energía Solar Bilbao",
        source: "google_ads",
        status: "new",
        notes: "Tipo de empresa: Instaladores de placas solares. Plan de interés: Plan Básico - Presencia Online. Ubicación: Bilbao. Empresa nueva que necesita presencia online básica."
      },
      {
        name: "Luis Fernández",
        email: "luis.fernandez@reformaslf.com",
        phone: "+34 688 123 456",
        company: "Reformas LF",
        source: "referral",
        status: "new",
        notes: "Tipo de empresa: Reformas del hogar. Plan de interés: Plan Empresarial - Solución Integral. Ubicación: Málaga. Empresa consolidada que busca solución completa de marketing digital."
      },
      {
        name: "Isabel Ruiz",
        email: "isabel.ruiz@ecocasas.com",
        phone: "+34 655 444 333",
        company: "EcoCasas Valencia",
        source: "quote_request",
        status: "new",
        notes: "Solicitud de presupuesto personalizado\nSituación actual: Empresa nueva en el sector de reformas ecológicas\nServicios: Marketing Digital Completo, SEO, Google Ads\nPresupuesto: €3,000-5,000 mensuales\nPlazo: Iniciar en 2 semanas\nWebsite: www.ecocasas.com\nComentarios: Queremos posicionarnos como referentes en reformas sostenibles en Valencia"
      }
    ];

    try {
      const { data, error } = await supabase
        .from('leads')
        .insert(sampleLeads)
        .select();

      if (error) throw error;

      // Agregar algunas citas de ejemplo
      const sampleAppointments = [
        {
          lead_id: data[0].id,
          date: new Date(Date.now() + 2 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // En 2 días
          time: "10:00",
          status: "pending",
          notes: "Consultoría marketing digital - Instaladores de placas solares"
        },
        {
          lead_id: data[1].id,
          date: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // En 3 días
          time: "15:00",
          status: "pending",
          notes: "Consultoría marketing digital - Reformas del hogar"
        }
      ];

      const { error: appointmentsError } = await supabase
        .from('appointments')
        .insert(sampleAppointments);

      if (appointmentsError) throw appointmentsError;

      toast({
        title: "Leads de ejemplo creados",
        description: "Se han agregado 5 leads y 2 citas de ejemplo al sistema (incluyendo solicitud de presupuesto)"
      });

      // Recargar datos
      const { data: newLeadsData } = await supabase
        .from('leads')
        .select('*')
        .order('created_at', { ascending: false });

      const { data: newAppointmentsData } = await supabase
        .from('appointments')
        .select(`
          *,
          leads (
            id, name, email, phone, company
          )
        `)
        .order('created_at', { ascending: false });

      setLeads(newLeadsData || []);
      setAppointments(newAppointmentsData || []);

    } catch (error) {
      console.error('Error generating sample leads:', error);
      toast({
        title: "Error",
        description: "No se pudieron crear los leads de ejemplo. Revisa la conexión con Supabase.",
        variant: "destructive"
      });
    }
  }

  const clearAllData = async () => {
    const confirmed = window.confirm(
      "¿Estás seguro de que quieres eliminar todos los leads y citas? Esta acción no se puede deshacer."
    );
    
    if (!confirmed) return;

    try {
      // Eliminar todas las citas primero (por la relación foreign key)
      const { error: appointmentsError } = await supabase
        .from('appointments')
        .delete()
        .not('id', 'is', null); // Elimina todos los registros

      if (appointmentsError) throw appointmentsError;

      // Eliminar todos los leads
      const { error: leadsError } = await supabase
        .from('leads')
        .delete()
        .not('id', 'is', null); // Elimina todos los registros

      if (leadsError) throw leadsError;

      // Limpiar el estado local
      setLeads([]);
      setAppointments([]);

      toast({
        title: "Datos eliminados",
        description: "Se han eliminado todos los leads y citas del sistema"
      });

    } catch (error) {
      console.error('Error clearing data:', error);
      toast({
        title: "Error",
        description: "No se pudieron eliminar los datos. Revisa la conexión con Supabase.",
        variant: "destructive"
      });
    }
  }

  const filteredLeads = leads.filter(lead => {
    const matchesSearch = (lead?.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (lead?.email || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (lead?.company || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === "todos" || lead?.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const stats = {
    totalLeads: leads.length,
    newLeads: leads.filter(l => l?.status === "nuevo" || l?.status === "new").length,
    qualifiedLeads: leads.filter(l => l?.status === "calificado" || l?.status === "qualified").length,
    upcomingAppointments: appointments.filter(a => a?.date && new Date(a.date) > new Date()).length
  };

  if (isAdmin === null) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <CardTitle className="text-xl text-red-600">Acceso Denegado</CardTitle>
          </CardHeader>
          <CardContent className="text-center space-y-4">
            <p className="text-muted-foreground">
              No tienes permisos para acceder al panel de administración.
            </p>
            <Button onClick={signOut} variant="outline" className="w-full">
              <LogOut className="mr-2 h-4 w-4" />
              Cerrar Sesión
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-4 sm:p-6">
      <div className="max-w-7xl mx-auto space-y-4 sm:space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold">Panel de Administración</h1>
            <p className="text-sm sm:text-base text-muted-foreground">Gestiona tus leads y citas</p>
          </div>
          <div className="flex flex-col sm:flex-row items-end gap-2 w-full sm:w-auto">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <span>{user?.email}</span>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
            <Button onClick={generateSampleLeads} variant="outline" className="w-full sm:w-auto">
              <Users className="mr-2 h-4 w-4" />
              Generar Leads de Ejemplo
            </Button>
            <Button onClick={clearAllData} variant="destructive" className="w-full sm:w-auto">
              <Trash2 className="mr-2 h-4 w-4" />
              Eliminar Todos
            </Button>
            <Button onClick={signOut} variant="outline" className="w-full sm:w-auto">
              <LogOut className="mr-2 h-4 w-4" />
              Cerrar Sesión
            </Button>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
          <Card>
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs sm:text-sm text-muted-foreground">Total Leads</p>
                  <p className="text-lg sm:text-2xl font-bold">{stats.totalLeads}</p>
                </div>
                <Users className="h-6 sm:h-8 w-6 sm:w-8 text-primary" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs sm:text-sm text-muted-foreground">Nuevos</p>
                  <p className="text-lg sm:text-2xl font-bold text-blue-500">{stats.newLeads}</p>
                </div>
                <AlertCircle className="h-6 sm:h-8 w-6 sm:w-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs sm:text-sm text-muted-foreground">Calificados</p>
                  <p className="text-lg sm:text-2xl font-bold text-green-500">{stats.qualifiedLeads}</p>
                </div>
                <TrendingUp className="h-6 sm:h-8 w-6 sm:w-8 text-green-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs sm:text-sm text-muted-foreground">Próximas Citas</p>
                  <p className="text-lg sm:text-2xl font-bold text-primary">{stats.upcomingAppointments}</p>
                </div>
                <CalendarDays className="h-6 sm:h-8 w-6 sm:w-8 text-primary" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Content */}
        <Tabs defaultValue="leads" className="space-y-4">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="leads">Gestión de Leads</TabsTrigger>
            <TabsTrigger value="appointments">Gestión de Citas</TabsTrigger>
          </TabsList>
          
          {/* Leads Tab */}
          <TabsContent value="leads" className="space-y-4">
            {/* Filters */}
            <Card>
              <CardContent className="p-4">
                <div className="flex flex-col md:flex-row gap-4">
                  <div className="flex-1">
                    <div className="relative">
                      <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                      <Input
                        placeholder="Buscar por nombre, email o empresa..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-10"
                      />
                    </div>
                  </div>
                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="w-full md:w-48">
                      <Filter className="mr-2 h-4 w-4" />
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="todos">Todos los estados</SelectItem>
                      <SelectItem value="nuevo">Nuevos</SelectItem>
                      <SelectItem value="contactado">Contactados</SelectItem>
                      <SelectItem value="calificado">Calificados</SelectItem>
                      <SelectItem value="perdido">Perdidos</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>

            {/* Leads List */}
            <div className="space-y-4">
              {filteredLeads.map((lead) => {
                const StatusIcon = leadStatuses[lead.status as keyof typeof leadStatuses]?.icon || AlertCircle;
                return (
                  <Card key={lead.id} className="hover-lift">
                    <CardContent className="p-4 sm:p-6">
                      <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                        <div className="flex-1 space-y-2">
                          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
                            <div className="flex items-center gap-2">
                              <User className="h-4 sm:h-5 w-4 sm:w-5 text-muted-foreground" />
                              <h3 className="font-semibold text-base sm:text-lg">{lead?.name || 'Sin nombre'}</h3>
                            </div>
                            <Badge className={`${leadStatuses[lead.status as keyof typeof leadStatuses]?.color} text-white w-fit`}>
                              <StatusIcon className="h-3 w-3 mr-1" />
                              {leadStatuses[lead.status as keyof typeof leadStatuses]?.label}
                            </Badge>
                          </div>
                          
                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2 text-xs sm:text-sm text-muted-foreground">
                            <div className="flex items-center gap-2">
                              <Mail className="h-3 sm:h-4 w-3 sm:w-4 flex-shrink-0" />
                              <span className="truncate">{lead?.email || 'Sin email'}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Phone className="h-3 sm:h-4 w-3 sm:w-4 flex-shrink-0" />
                              <span>{lead?.phone || 'Sin teléfono'}</span>
                            </div>
                            {lead?.company && (
                              <div className="flex items-center gap-2">
                                <Building className="h-3 sm:h-4 w-3 sm:w-4 flex-shrink-0" />
                                <span className="truncate">{lead.company}</span>
                              </div>
                            )}
                            <div className="flex items-center gap-2">
                              <MapPin className="h-3 sm:h-4 w-3 sm:w-4 flex-shrink-0" />
                              <span>{lead?.location || lead?.notes || 'Sin ubicación'}</span>
                            </div>
                          </div>
                          
                          <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 text-xs sm:text-sm">
                            <span className="font-medium">Servicio:</span>
                            <span className="text-primary">{lead?.service_interest || lead?.notes || 'Sin servicio especificado'}</span>
                          </div>
                          
                          {lead.message && (
                            <p className="text-xs sm:text-sm text-muted-foreground bg-muted p-2 rounded">
                              {lead.message}
                            </p>
                          )}
                          
                          <p className="text-xs text-muted-foreground">
                            Creado: {format(new Date(lead.created_at), "dd/MM/yyyy HH:mm")}
                          </p>
                        </div>
                        
                        <div className="flex flex-row sm:flex-col gap-2">
                          <Select
                            value={lead.status}
                            onValueChange={(value) => updateLeadStatus(lead.id, value)}
                          >
                            <SelectTrigger className="w-full sm:w-32">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="nuevo">Nuevo</SelectItem>
                              <SelectItem value="contactado">Contactado</SelectItem>
                              <SelectItem value="calificado">Calificado</SelectItem>
                              <SelectItem value="perdido">Perdido</SelectItem>
                            </SelectContent>
                          </Select>
                          
                          <Button variant="outline" size="sm" className="flex-1 sm:flex-none">
                            <Phone className="h-3 sm:h-4 w-3 sm:w-4 mr-1" />
                            <span className="hidden sm:inline">Llamar</span>
                            <span className="sm:hidden">Call</span>
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </TabsContent>
          
          {/* Appointments Tab */}
          <TabsContent value="appointments" className="space-y-4">
            <div className="space-y-4">
              {appointments.map((appointment) => (
                <Card key={appointment.id} className="hover-lift">
                  <CardContent className="p-6">
                    <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                      <div className="flex-1 space-y-2">
                        <div className="flex items-center gap-3">
                          <Calendar className="h-5 w-5 text-muted-foreground" />
                          <h3 className="font-semibold text-lg">{appointment?.leads?.name || appointment?.lead?.name || 'Sin nombre'}</h3>
                          <Badge className={`${appointmentStatuses[appointment?.status as keyof typeof appointmentStatuses]?.color || 'bg-gray-500'} text-white`}>
                            {appointmentStatuses[appointment?.status as keyof typeof appointmentStatuses]?.label || 'Sin estado'}
                          </Badge>
                        </div>
                        
                        <div className="grid md:grid-cols-3 gap-2 text-sm text-muted-foreground">
                          <div className="flex items-center gap-2">
                            <Clock className="h-4 w-4" />
                            {appointment?.date ? format(new Date(appointment.date + ' ' + (appointment?.time || '00:00')), "dd/MM/yyyy HH:mm") : 'Sin fecha'}
                          </div>
                          <div className="flex items-center gap-2">
                            <Phone className="h-4 w-4" />
                            {appointment?.leads?.phone || appointment?.lead?.phone || 'Sin teléfono'}
                          </div>
                          <div className="flex items-center gap-2">
                            <Building className="h-4 w-4" />
                            {appointment?.service_type || appointment?.notes || 'Sin servicio especificado'}
                          </div>
                        </div>
                        
                        {appointment?.notes && (
                          <p className="text-sm text-muted-foreground bg-muted p-2 rounded">
                            {appointment.notes}
                          </p>
                        )}
                      </div>
                      
                      <div className="flex flex-col gap-2">
                        <Button variant="default" size="sm">
                          <CheckCircle className="h-4 w-4 mr-1" />
                          Confirmar
                        </Button>
                        <Button variant="outline" size="sm">
                          Reagendar
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}